/**
 * Pipeline Power Score card (Daniel 2026-06-09, Round L-11).
 *
 * Daily points target with progress ring + tier breakdown + per-activity
 * status list + 7-day grid + one-tap manual loggers. Auto-derives 80% of
 * the activities from existing TR data; only 11ish manual items need a
 * tap (Pop Bys, Doors knocked, Handwritten notes, etc.).
 *
 * Mounted on Home (current user) and Agent Detail (peer view).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPowerScore,
  logPowerScoreManual,
  type PowerActivityProgress,
  type PowerScoreResponse,
} from '../api/production';

interface Props {
  /** Optional — defaults to the calling user's own score. Privileged
   *  callers (owner/operations/admin) can pass a peer's id. */
  userId?: number;
  /** Show a compact one-line summary instead of the full card. Used on
   *  the agent dashboard "next to other tiles" position. */
  compact?: boolean;
}

const TIER_LABELS: Record<string, string> = {
  tier1: 'Tier 1 · Direct Conversion',
  tier2: 'Tier 2 · Relationship',
  tier3: 'Tier 3 · Visibility',
  bonus: 'Bonus',
};
const TIER_COLORS: Record<string, { bg: string; fg: string; chip: string }> = {
  tier1: { bg: 'var(--brand-primary-soft, #EEF0FE)', fg: 'var(--brand-primary, #4f46e5)', chip: 'var(--brand-primary, #4f46e5)' },
  tier2: { bg: 'var(--success-soft, #DCF1E5)',       fg: 'var(--success, #196B47)',        chip: 'var(--success, #196B47)' },
  tier3: { bg: 'var(--warning-soft, #FBEACB)',       fg: 'var(--warning, #7B5A1E)',        chip: 'var(--warning, #7B5A1E)' },
  bonus: { bg: 'var(--bg-surface-2)',                fg: 'var(--text-body)',               chip: 'var(--text-muted)' },
};

export default function PowerScoreCard({ userId, compact = false }: Props) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(!compact);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['power-score', userId ?? 'me'],
    queryFn: () => getPowerScore({ userId }),
    refetchInterval: 60_000, // light polling so manual taps elsewhere catch up
    staleTime: 30_000,
  });

  const log = useMutation({
    mutationFn: ({ key, count }: { key: string; count: number }) =>
      logPowerScoreManual(key, count),
    onSuccess: (r) => {
      // Optimistic-ish — write the new "today" into the cache so the
      // ring + breakdown update before the next refetch.
      qc.setQueryData<PowerScoreResponse | undefined>(
        ['power-score', userId ?? 'me'],
        (prev) => prev ? { ...prev, today: r.today } : prev,
      );
    },
  });

  if (isLoading || !data) {
    return (
      <section style={cardStyle}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading Power Score…</div>
      </section>
    );
  }
  if (isError) {
    return (
      <section style={cardStyle}>
        <div style={{ fontSize: 13, color: 'var(--danger)' }}>Couldn't load Power Score.</div>
      </section>
    );
  }

  const { today, daily_target, tier1_target, seven_day_grid } = data;
  const pct = Math.min(100, Math.round((today.total_points / Math.max(1, daily_target)) * 100));
  const tier1Hit = today.tier_totals.tier1 >= tier1_target;

  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-heading)', margin: 0 }}>
            Pipeline Power Score
          </h2>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Daily target: {daily_target} pts · at least {tier1_target} from Tier 1
          </p>
        </div>
        <ProgressRing percent={pct} value={today.total_points} target={daily_target} />
      </div>

      <TierBars totals={today.tier_totals} target={daily_target} tier1Target={tier1_target} tier1Hit={tier1Hit} />

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          padding: '6px 0',
          fontSize: 12,
          color: 'var(--brand-primary, #4f46e5)',
          cursor: 'pointer',
          marginTop: 10,
          fontFamily: 'inherit',
          fontWeight: 500,
        }}
      >
        {expanded ? '▾ Hide activities' : '▸ Show activities'}
      </button>

      {expanded && (
        <ActivityList
          today={today}
          onLog={(key, count) => log.mutate({ key, count })}
          isLogging={log.isPending}
          readOnly={!!userId} // peer view — no manual logging
        />
      )}

      {expanded && <WeekGrid grid={seven_day_grid} target={daily_target} />}
    </section>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────

function ProgressRing({ percent, value, target }: { percent: number; value: number; target: number }) {
  const size = 80;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;
  const ringColor = percent >= 100 ? 'var(--success, #196B47)' : 'var(--brand-primary, #4f46e5)';
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--border)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 350ms ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-heading)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>of {target}</div>
      </div>
    </div>
  );
}

function TierBars({
  totals,
  target,
  tier1Target,
  tier1Hit,
}: {
  totals: { tier1: number; tier2: number; tier3: number; bonus: number };
  target: number;
  tier1Target: number;
  tier1Hit: boolean;
}) {
  // Each tier bar fills relative to the overall target so the user
  // sees the MIX at a glance, not just absolute totals.
  const rows: Array<{ key: keyof typeof TIER_COLORS; value: number; subtle?: string }> = [
    { key: 'tier1', value: totals.tier1, subtle: tier1Hit ? '✓ ' + tier1Target + '+ pts' : 'Need ' + Math.max(0, tier1Target - totals.tier1) + ' more' },
    { key: 'tier2', value: totals.tier2 },
    { key: 'tier3', value: totals.tier3 },
  ];
  if (totals.bonus > 0) rows.push({ key: 'bonus', value: totals.bonus });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r) => {
        const c = TIER_COLORS[r.key];
        const pct = Math.min(100, (r.value / Math.max(1, target)) * 100);
        return (
          <div key={r.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
              <span>{TIER_LABELS[r.key]}</span>
              <span style={{ color: c.fg, fontWeight: 600 }}>
                {r.value} pt{r.value === 1 ? '' : 's'}
                {r.subtle && <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontWeight: 400 }}>· {r.subtle}</span>}
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--bg-surface-2)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: c.chip, transition: 'width 300ms ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityList({
  today,
  onLog,
  isLogging,
  readOnly,
}: {
  today: PowerActivityProgress[] extends never ? never : PowerScoreResponse['today'];
  onLog: (key: string, count: number) => void;
  isLogging: boolean;
  readOnly: boolean;
}) {
  // Group by tier for visual grouping.
  const byTier: Record<string, PowerActivityProgress[]> = { tier1: [], tier2: [], tier3: [], bonus: [] };
  for (const a of today.activities) {
    const k = a.tier === 'bonus' ? 'bonus' : `tier${a.tier}`;
    byTier[k].push(a);
  }
  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {(['tier1', 'tier2', 'tier3', 'bonus'] as const).map((k) => {
        if (byTier[k].length === 0) return null;
        return (
          <div key={k}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: TIER_COLORS[k].fg,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 6,
              }}
            >
              {TIER_LABELS[k]}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {byTier[k].map((a) => (
                <ActivityRow key={a.key} a={a} onLog={onLog} isLogging={isLogging} readOnly={readOnly} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityRow({
  a,
  onLog,
  isLogging,
  readOnly,
}: {
  a: PowerActivityProgress;
  onLog: (key: string, count: number) => void;
  isLogging: boolean;
  readOnly: boolean;
}) {
  const done = a.points_earned > 0;
  return (
    <div
      title={a.description}
      style={{
        display: 'grid',
        gridTemplateColumns: '20px 1fr auto auto',
        gap: 8,
        alignItems: 'center',
        padding: '6px 8px',
        background: done ? 'var(--success-soft, #DCF1E5)' : 'transparent',
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      {/* L-54c (Daniel 2026-06-29): the left circle now IS the primary
          +1 click target. It used to be a static <span> that looked
          like a checkbox — Daniel kept clicking it expecting credit
          and nothing happened. Now any agent can click the circle on
          any row to log one count toward that activity (manual or
          auto). Once the row is done, the circle shows ✓ and the
          button disables (no negative-count UX, that's reserved for a
          future "undo" affordance). */}
      <button
        type="button"
        onClick={() => !done && !readOnly && onLog(a.key, 1)}
        disabled={done || readOnly || isLogging}
        title={done ? 'Completed' : 'Log one toward this activity'}
        aria-label={done ? `${a.label} completed` : `Log one toward ${a.label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: done ? 'var(--success, #196B47)' : 'transparent',
          border: done ? 'none' : '1.5px dashed var(--border-strong, #c1c1c1)',
          color: done ? '#fff' : 'transparent',
          fontSize: 11,
          lineHeight: 1,
          padding: 0,
          cursor: done ? 'default' : (readOnly || isLogging ? 'wait' : 'pointer'),
          fontFamily: 'inherit',
          transition: 'background 0.12s, border-color 0.12s, color 0.12s',
        }}
        onMouseEnter={(e) => {
          if (done || readOnly || isLogging) return;
          e.currentTarget.style.background = 'var(--brand-primary-soft, #e7e6ff)';
          e.currentTarget.style.borderColor = 'var(--brand-primary, #4f46e5)';
          e.currentTarget.style.color = 'var(--brand-primary, #4f46e5)';
        }}
        onMouseLeave={(e) => {
          if (done) return;
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'var(--border-strong, #c1c1c1)';
          e.currentTarget.style.color = 'transparent';
        }}
      >
        ✓
      </button>
      <span style={{ color: done ? 'var(--text-heading)' : 'var(--text-body)', fontWeight: done ? 500 : 400 }}>
        {a.label}
        {a.manual && (
          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>
            manual
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {a.completed} / {a.bundle_size}
        {a.packs_completed > 0 && a.bundle_size > 1 ? ` × ${a.packs_completed}` : ''}
      </span>
      {a.manual && !readOnly ? (
        <button
          type="button"
          onClick={() => onLog(a.key, 1)}
          disabled={isLogging}
          style={{
            border: '1px solid var(--brand-primary, #4f46e5)',
            background: 'var(--brand-primary, #4f46e5)',
            color: '#fff',
            padding: '3px 9px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            cursor: isLogging ? 'wait' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          +1
        </button>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 36, textAlign: 'right' }}>
          {a.points_earned > 0 ? `+${a.points_earned} pts` : `${a.points} pt${a.points === 1 ? '' : 's'}`}
        </span>
      )}
    </div>
  );
}

function WeekGrid({
  grid,
  target,
}: {
  grid: PowerScoreResponse['seven_day_grid'];
  target: number;
}) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Last 7 days
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {grid.map((d) => {
          const hit = d.total_points >= target;
          const pct = Math.min(100, (d.total_points / Math.max(1, target)) * 100);
          const dayName = new Date(d.date_iso + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
          return (
            <div
              key={d.date_iso}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '6px 4px',
                background: 'var(--bg-surface-2)',
                borderRadius: 4,
              }}
              title={`${d.date_iso}: ${d.total_points} points`}
            >
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {dayName}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: hit ? 'var(--success, #196B47)' : 'var(--text-heading)',
                  margin: '3px 0',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {d.total_points}
              </div>
              <div
                style={{
                  width: '100%',
                  height: 3,
                  background: 'var(--border)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: hit ? 'var(--success, #196B47)' : 'var(--brand-primary, #4f46e5)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '18px 20px',
};
